import VueSimpleDeepset from "@/util/vue-simple-deepset";
import {
  Module,
  VuexModule,
  Mutation,
  Action,
  config
} from "vuex-module-decorators";
import { uiStore, configStore } from "@/store/store";
import { DocumentStorePropertyChange } from "@/types/store-property-change";
import { Tree, TreeNode } from "@/types/tree";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import _get from "lodash/get";
import { DragPayload } from "@/lib/structure-panel-drag/util";
import defensiveCopy from "@/util/defensive-copy";
import { breakdanceUndo } from "@/lib/undo/undo";
import {
  copyNode,
  createNodesFromSlugListAndPlace,
  createNode,
  deleteNode,
  duplicateNode,
  findNode,
  getBlankTree,
  getNodeParent,
  createTreeFromExportedTree,
  moveNode,
  recalculateParentIdsAndLookupTable,
  getIdsFromNodeAndDescendants,
  areNodeTypesPresentInTree,
  getNodeIndex
} from "@/lib/tree/tree";
import {
  findPlaceToAddNodes,
  getDefaultTopLevelElementSlug,
  getIndexToPlaceNode,
  getNestingRuleFromElementSlug,
  isThisNodeAllowedInsideThatNode
} from "@/lib/tree/nesting";
import {
  BreakdanceDocument,
  BreakdanceDocumentWithExportedTree
} from "@/types/document";
import { DocumentRevision } from "@/types/revisions";
import NotificationLogger from "@/lib/notifications/notifications";
import { openUiPanel } from "@/lib/panels/panels";
import { throttlePropertyChange } from "@/util/throttle-property-change";
import { NO_DOCUMENT, NO_DOCUMENT_ID } from "@/store/util";
import { generateTransactionKeyFromGenericStorePropertyChange } from "@/lib/undo/transaction-key.utils";
import { getBuilderElementNameBySlug } from "@/lib/undo/snapshot-labels/document-store-property-change.utils";
import { PlaceToAddElementInTree } from "@/types/elements";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { forceNextTick } from "@/util/force-next-tick";
import { compare } from "rfc6902-json-diff";
import { applyPatchWithVueReactivity } from "@/util/fast-json-patch-reactive-ops";

/**
 * IMPORTANT:
 * READ & UNDERSTAND THE FOLLOWING BEFORE ADDING
 * PROPERTIES TO THE DOCUMENT OR GLOBAL STORE
 *
 * Most likely, whatever property you wish to add belongs
 * in the configStore.
 *
 * The document and global stores are only for storing things
 * that are manipulated by the user AND that will be saved to
 * the backend when the user clicks the Save button
 *
 * If it will be associated with the document the user is editing,
 * (i.e. saved in the post meta), it belongs in the document store.
 *
 * If it will be associated with the the whole website, (i.e. saved in
 * the sitewide options table), it belongs in the global store.
 *
 * If clicking save shouldn’t send the data back to the WP database,
 * it does not belong in the document or global store.
 *
 * UNDO / REDO:
 *
 * All mutations to the properties of the document and global
 * stores can be undone/redone.
 *
 * It is important to understand how Undo works (this explanation
 * is slightly oversimplified):
 *
 * When opening the builder or switching documents, the initial
 * states of the global store and document store are saved.
 *
 * All mutations made to the document store and global store are
 * logged.
 *
 * To undo the last mutation, the document and global store are
 * reset to their initial states. Then, all the mutations except
 * the last mutation are re-run.
 *
 * This method only works if the mutations are deterministic / pure -
 * i.e. given a particular initial state, running the same set of
 * mutations in the same order will always produce the same new state.
 *
 * THEREFORE, mutations must be deterministic.
 *
 * THEREFORE, IF YOU ADD A PROPERTY TO THE DOCUMENT OR GLOBAL STORE,
 * YOU MUST MODIFY THE UNDO FUNCTIONALITY (hint: probably in the BreakdanceUndo class) TO:
 *  - 1. Ignore the mutation that initially sets the property (hint: probably BreakdanceUndo.mutationsToIgnore)
 *  - 2. Store the initial value of the property (hint: probably BreakdanceUndo.initialize())
 *  - 3. Reset the value of the property (hint: probably BreakdanceUndo.resetToInitialState())
 *
 * UNSAVED CHANGES PRESENT WARNING:
 *
 * This warning takes the same approach as Undo - watch the
 * document/ global store for mutations, set a flag if a mutation
 * occurs (but ignore the mutations that initially set the values),
 * and unset the flag when the page is saved.
 *
 * If you add a property, you'll have to ensure setting it initially
 * is ignored - probably in Save.vue.
 */

config.rawError = true;
@Module({ name: "document", namespaced: true })
export default class DocumentModule extends VuexModule {
  document: BreakdanceDocument = defensiveCopy(NO_DOCUMENT);

  @Action
  duplicateElementAndActivateIt(elementId: number) {
    const elementSlug = findNode(this.document.tree, elementId)?.data.type;
    const transactionKey = `DocumentDuplicateElement:Slug:${elementSlug}`;

    breakdanceUndo.transaction(
      () => {
        const pointerForCreatedNodeId: { nodeId?: number } = {
          nodeId: undefined
        };

        this.duplicateElement({
          elementId,
          pointer: pointerForCreatedNodeId
        });

        if (pointerForCreatedNodeId.nodeId !== undefined) {
          uiStore.activateElement(pointerForCreatedNodeId.nodeId);

          breakdanceStoreActions.run.onCreatedElement({
            elementId: pointerForCreatedNodeId.nodeId,
            transactionKey
          });
        }
      },
      `Duplicate ${getBuilderElementNameBySlug(elementSlug)}`,
      transactionKey
    );
  }

  @Action
  deleteElement({ nodeId }: { nodeId: number }) {
    const parentNode = getNodeParent(this.document.tree, nodeId);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const maybeNode = findNode(this.document.tree, nodeId);

    if (!maybeNode) return;

    const allIdsOfElementsToBeDeleted = getIdsFromNodeAndDescendants(maybeNode);

    allIdsOfElementsToBeDeleted.forEach(id =>
      breakdanceStoreActions.run.onBeforeDeletingElement({ elementId: id })
    );

    // Wait for "onBeforeDeletingElement" actions as they may need to access the DOM before we delete the element(s)
    forceNextTick(() => {
      if (parentNode !== false) {
        uiStore.activateElement(parentNode.id);
      }

      const transactionKey = `DocumentDeleteElement:ID:${nodeId}`;

      breakdanceUndo.transaction(
        () => {
          this.deleteElementMutation(nodeId);
        },
        `Delete ${getBuilderElementNameBySlug(maybeNode?.data.type)}`,
        transactionKey
      );
    });
  }

  @Action
  addElement({
    elementSlug,
    addToNodeId,
    placeToAdd
  }: {
    elementSlug: string;
    addToNodeId?: number;
    placeToAdd?: PlaceToAddElementInTree;
  }) {
    const transactionKey = `DocumentAddElement:Slug:${elementSlug}`;

    breakdanceUndo.transaction(
      () => {
        const pointerForCreatedNodeId: { nodeId?: number } = {
          nodeId: undefined
        };

        const placeToAddElement =
          placeToAdd ||
          findPlaceToAddNodes(
            this.document.tree,
            addToNodeId === undefined ? uiStore.activeElementId : addToNodeId,
            {
              self: getNestingRuleFromElementSlug(elementSlug),
              children: []
            }
          );

        pipe(
          placeToAddElement,
          fold(
            noLegalPlaceToAdd => {
              if (noLegalPlaceToAdd == "NONE") {
                NotificationLogger.log.message("cant add a " + elementSlug);
              }

              if (noLegalPlaceToAdd == "NOT_ALLOWED_BECAUSE_OF_OTHER_NODES") {
                const nodes =
                  getNestingRuleFromElementSlug(elementSlug)
                    .notAllowedWhenNodeTypeIsPresentInTree || [];

                const currElementName = configStore.element(elementSlug).name;
                const names = nodes.map(n => configStore.element(n).name);
                const isSameNode = areNodeTypesPresentInTree(
                  this.document.tree,
                  [elementSlug]
                );

                if (isSameNode) {
                  NotificationLogger.log.message(
                    `You can't add more than one ${currElementName}.`
                  );
                } else {
                  const listOfNames =
                    names.slice(0, -1).join(",") + " or " + names.slice(-1);

                  NotificationLogger.log.message(
                    `You can't add "${currElementName}" on the same page as ${listOfNames}.`
                  );
                }
              }
            },
            legalPlaceToAdd => {
              if (legalPlaceToAdd === "TOP_LEVEL") {
                this.addElementMutation({
                  elementSlug: getDefaultTopLevelElementSlug(),
                  parentNodeId: this.document.tree.root.id,
                  index: false,
                  pointer: pointerForCreatedNodeId
                });

                if (pointerForCreatedNodeId.nodeId !== undefined) {
                  this.addElementMutation({
                    elementSlug: elementSlug,
                    parentNodeId: pointerForCreatedNodeId.nodeId,
                    index: false,
                    pointer: pointerForCreatedNodeId
                  });
                }
              } else {
                this.addElementMutation({
                  elementSlug: elementSlug,
                  parentNodeId: legalPlaceToAdd.place.id,
                  index: legalPlaceToAdd.index,
                  pointer: pointerForCreatedNodeId
                });
              }

              if (pointerForCreatedNodeId.nodeId !== undefined) {
                uiStore.activateElement(pointerForCreatedNodeId.nodeId);
                openUiPanel("elementproperties");

                breakdanceStoreActions.run.onCreatedElement({
                  elementId: pointerForCreatedNodeId.nodeId,
                  transactionKey
                });
              }
            }
          )
        );
      },
      `Add ${getBuilderElementNameBySlug(elementSlug)}`,
      transactionKey
    );
  }

  @Mutation
  pasteCopiedElementMutation({
    element,
    placeToAddId,
    placeToAddIndex,
    pointer
  }: {
    element: TreeNode;
    placeToAddId: number;
    placeToAddIndex: number;
    pointer?: { nodeId?: number };
  }) {
    const copiedNode = copyNode(this.document.tree, element);
    moveNode(this.document.tree, copiedNode.id, placeToAddId, placeToAddIndex);

    if (pointer) {
      pointer.nodeId = copiedNode.id;
    }
  }

  @Action
  addMultipleNodes({
    nodes,
    parentId
  }: {
    nodes: TreeNode[];
    parentId: number;
  }) {
    const allowedInsideParent = nodes
      .map(node =>
        isThisNodeAllowedInsideThatNode(this.document.tree, node, parentId)
      )
      .every(allowed => allowed === true);

    const parentNode = findNode(this.document.tree, parentId);

    if (parentNode && allowedInsideParent) {
      const index = uiStore.activeElement // TODO - pass in the index
        ? getIndexToPlaceNode(
            this.document.tree,
            parentNode,
            uiStore.activeElement
          )
        : false;

      this.addMultipleNodesMutation({
        nodes: nodes,
        parentId: parentId,
        index: index === false ? parentNode.children.length : index
      });
    } else {
      console.log(
        `error in addNodesFromLibrary - one of the nodes is not allowed in the tree root / has a descendant that isnt allowed to be a descendant of tree root.`,
        nodes
      );
    }
  }

  @Mutation
  addMultipleNodesMutation({
    nodes,
    parentId,
    index
  }: {
    nodes: TreeNode[];
    parentId: number;
    index: number;
  }) {
    nodes.forEach(node => {
      const nodeToInsert = copyNode(this.document.tree, node);
      moveNode(this.document.tree, nodeToInsert.id, parentId, index);
      index++;
    });
  }

  // Use this when updating an element over and over and some updates can be skipped
  @Action
  throttledPropertyChanged({
    elementId,
    path,
    value,
    meta
  }: DocumentStorePropertyChange) {
    throttlePropertyChange(
      `${elementId} - ${path}`,
      this.unthrottledPropertyChanged,
      {
        elementId,
        path,
        value,
        meta
      },
      "elements"
    );
  }

  /** Use this when all changes needs to run
   * For example mutating 5 different properties on a `for` loop. If you throttle it, some of them won't be updated
   * because they're all queued up together and some are not run (throttling)
   * */
  @Action
  unthrottledPropertyChanged({
    elementId,
    path,
    value,
    meta
  }: DocumentStorePropertyChange) {
    const maybeNode = findNode(this.document.tree, elementId);
    if (!maybeNode) return;

    const previousValue = defensiveCopy(
      _get(maybeNode.data.properties, path)
    ) as unknown;

    const transactionKey = generateTransactionKeyFromGenericStorePropertyChange(
      { path, value, meta },
      `DocumentProperty:ID:${elementId}`
    );

    // TODO move breakdanceUndo.transaction-related logic somehow to BreakdanceUndo?
    breakdanceUndo.transaction(
      () => {
        this.propertyChangedMutation({
          elementId: elementId,
          path: path,
          value: defensiveCopy(value),
          meta // TODO maybe better omit meta at propertyChangedMutation()'s argument type
        });

        uiStore.setLastModifiedElementProperty({ elementId, path });

        breakdanceStoreActions.run.onPropertyChange({
          elementId,
          path,
          value: defensiveCopy(value),
          previousValue,
          transactionKey
        });
      },
      meta.snapshotLabel,
      transactionKey
    );
  }

  @Action
  moveElement({
    draggedElementId,
    droppedOnElementId,
    index,
    wrapWithTransaction = true
  }: DragPayload & { wrapWithTransaction?: boolean }) {
    const previousParent = getNodeParent(this.document.tree, draggedElementId);
    const draggedElement = findNode(this.document.tree, draggedElementId);
    const currentNodeIndex = getNodeIndex(this.document.tree, draggedElementId);

    if (!draggedElement) return false;

    // The element was dragged but not moved anywhere
    if (
      previousParent &&
      droppedOnElementId === previousParent.id &&
      currentNodeIndex === index
    ) {
      return false;
    }

    const notAllowed = !isThisNodeAllowedInsideThatNode(
      this.document.tree,
      draggedElement,
      droppedOnElementId
    );

    if (notAllowed) {
      NotificationLogger.log.message(
        `The element ${draggedElement.data.type} can't be moved inside element with ID ${droppedOnElementId}`
      );
      return false;
    }

    const runMutation = (transactionKey?: string) => {
      this.moveElementMutation({
        draggedElementId,
        droppedOnElementId,
        index: index
      });

      if (previousParent) {
        breakdanceStoreActions.run.onMovedElement({
          elementId: draggedElementId,
          droppedOnElementId,
          index,
          previousParentId: previousParent.id,
          transactionKey
        });
      }
    };

    if (wrapWithTransaction) {
      const transactionKey = `DocumentMoveElementElement:ID:${draggedElement.id}`;

      breakdanceUndo.transaction(
        () => runMutation(transactionKey),
        `Move ${getBuilderElementNameBySlug(draggedElement.data.type)}`,
        transactionKey
      );
    } else {
      runMutation();
    }

    return true;
  }

  @Mutation
  setDocument({
    tree: treeToImport,
    documentMeta: documentMetaToImport
  }: BreakdanceDocumentWithExportedTree) {
    /**
     *  defensiveCopy isn't needed because setDocument is called only with:
     * - loadedData (who cares if we mutate it)
     * - a snapshot (get currentSnapshot defensive copies the snapshot for us)
     */

    if (treeToImport === false) {
      this.document.tree = getBlankTree();
    }
    // No need to generate tree DIFFs when overwriting an initial state
    else if (this.document.documentMeta.id === NO_DOCUMENT_ID) {
      this.document.tree = {
        root: treeToImport.root,
        _nextNodeId: treeToImport._nextNodeId,
        _lookupTable: {},
        status: "valid"
      };

      recalculateParentIdsAndLookupTable(this.document.tree);
    } else {
      const treeToCompareExistingWith: Tree = {
        root: treeToImport.root,
        _nextNodeId: treeToImport._nextNodeId,
        _lookupTable: {},
        status: "valid"
      };

      // This is needed to avoid redundant&dangerous lookupTable diff entries
      // Lookup table will be recalculated right after diff appliance
      this.document.tree._lookupTable = {};

      const documentDiff = compare(
        this.document.tree,
        treeToCompareExistingWith
      );
      applyPatchWithVueReactivity(this.document.tree, documentDiff);
    }

    recalculateParentIdsAndLookupTable(this.document.tree);

    applyPatchWithVueReactivity(
      this.document.documentMeta,
      compare(this.document.documentMeta, documentMetaToImport)
    );
  }

  @Mutation
  duplicateElement({
    elementId,
    pointer
  }: {
    elementId: number;
    pointer?: { nodeId?: number };
  }) {
    const node = duplicateNode(this.document.tree, elementId);

    if (pointer) {
      pointer.nodeId = node?.id;
    }
  }

  @Mutation
  deleteElementMutation(elementId: number) {
    deleteNode(this.document.tree, elementId);
  }

  @Mutation
  addElementMutation({
    elementSlug,
    parentNodeId,
    index,
    pointer
  }: {
    elementSlug: string;
    parentNodeId: number;
    index: number | false;
    pointer?: { nodeId?: number };
  }) {
    const element = configStore.element(elementSlug);

    const newNode = createNode(this.document.tree, {
      type: elementSlug,
      properties: element.defaultProperties
        ? defensiveCopy(element.defaultProperties)
        : null
    });

    const parentNode = findNode(this.document.tree, parentNodeId);
    if (!parentNode) return;

    moveNode(
      this.document.tree,
      newNode.id,
      parentNode.id,
      index === false ? parentNode.children.length : index
    );

    // moveNode must come first.
    // It checks for nesting rules, and they won't work if the previous parents/children aren't added until the end
    createNodesFromSlugListAndPlace(
      this.document.tree,
      element.defaultChildren,
      newNode.id
    );

    if (pointer) {
      pointer.nodeId = newNode.id;
    }
  }

  @Mutation
  propertyChangedMutation({
    elementId,
    path,
    value
  }: DocumentStorePropertyChange) {
    const element = findNode(this.document.tree, elementId);

    if (element) {
      VueSimpleDeepset(element, `data.properties.${path}`, value);
    }
  }

  get getPropertyValue() {
    return (elementId: number, path: string) => {
      const element = findNode(this.document.tree, elementId);
      if (element) {
        return _get(element, `data.properties.${path}`) as unknown;
      }
    };
  }

  @Mutation
  moveElementMutation({
    draggedElementId,
    droppedOnElementId,
    index
  }: DragPayload) {
    moveNode(this.document.tree, draggedElementId, droppedOnElementId, index);
  }

  @Mutation
  loadRevision(revision: DocumentRevision) {
    this.document.tree = createTreeFromExportedTree(
      defensiveCopy(revision.tree)
    );
  }

  @Mutation
  setNoDocument() {
    this.document = defensiveCopy(NO_DOCUMENT);
  }
}
